import React, { useEffect, useRef, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { type Dayjs } from "dayjs";
import { textStyle } from "../../styles/Style";
import useScrollToTop from "../../hooks/useScrollToTop";
import {
  type AgeGroupBand,
  type AgeGroupsPayload,
  CANCELLATION_POLICY_OPTIONS,
  initialAgeGroupsPayload,
  mergePartialConfiguringGroupDefaults,
  mergePartialDefaultCodes,
  mergePartialHousekeepingDefaults,
  mergePartialRequiredFields,
  mergePartialStayDefaults,
  mergePartialSystemDefaults,
  mergeQuestionnairePayload,
  type QuestionnairePayload,
  PROPERTY_TIMEZONE_OPTIONS,
  type StayDefaultsBookingConstraintsPayload,
  type StayDefaultsCancellationPayload,
  type ConfiguringGroupDefaultsPayload,
  type DefaultCodesPayload,
  type HousekeepingDefaultsPayload,
  type RequiredFieldsPayload,
  type StayDefaultsGeneralPayload,
  type StayDefaultsGuestRequirementsPayload,
  type StayDefaultsPayload,
  type SystemDefaultsPayload,
} from "../../../constants/questionnairePayloads";

export type VersaQuestionnaireProps = {
  data?: (value: QuestionnairePayload) => void;
  fieldValues?: Partial<QuestionnairePayload> | null;
};

const switchRowSx = {
  m: 0,
  width: "100%",
  justifyContent: "space-between",
  gap: 2,
  alignItems: "flex-start",
} as const;

const VersaQuestionnaire = ({ data, fieldValues }: VersaQuestionnaireProps) => {
  const { topRef } = useScrollToTop();
  const dataRef = useRef(data);
  dataRef.current = data;

  const [stayDefaults, setStayDefaults] = useState<StayDefaultsPayload>(() =>
    mergePartialStayDefaults(fieldValues?.stayDefaults),
  );
  const [ageGroups, setAgeGroups] = useState<AgeGroupsPayload>(
    () => fieldValues?.ageGroups ?? initialAgeGroupsPayload(),
  );
  const [requiredFields, setRequiredFields] = useState<RequiredFieldsPayload>(
    () => mergePartialRequiredFields(fieldValues?.requiredFields),
  );
  const [defaultCodes, setDefaultCodes] = useState<DefaultCodesPayload>(() =>
    mergePartialDefaultCodes(fieldValues?.defaultCodes),
  );
  const [systemDefaults, setSystemDefaults] = useState<SystemDefaultsPayload>(
    () => mergePartialSystemDefaults(fieldValues?.systemDefaults),
  );
  const [housekeepingDefaults, setHousekeepingDefaults] =
    useState<HousekeepingDefaultsPayload>(() =>
      mergePartialHousekeepingDefaults(fieldValues?.housekeepingDefaults),
    );
  const [configuringGroupDefaults, setConfiguringGroupDefaults] =
    useState<ConfiguringGroupDefaultsPayload>(() =>
      mergePartialConfiguringGroupDefaults(
        fieldValues?.configuringGroupDefaults,
      ),
    );

  useEffect(() => {
    if (fieldValues?.stayDefaults) {
      setStayDefaults(mergePartialStayDefaults(fieldValues.stayDefaults));
    }
  }, [fieldValues?.stayDefaults]);

  useEffect(() => {
    if (fieldValues?.ageGroups) {
      setAgeGroups(fieldValues.ageGroups);
    }
  }, [fieldValues?.ageGroups]);

  useEffect(() => {
    if (fieldValues?.requiredFields) {
      setRequiredFields(mergePartialRequiredFields(fieldValues.requiredFields));
    }
  }, [fieldValues?.requiredFields]);

  useEffect(() => {
    if (fieldValues?.defaultCodes) {
      setDefaultCodes(mergePartialDefaultCodes(fieldValues.defaultCodes));
    }
  }, [fieldValues?.defaultCodes]);

  useEffect(() => {
    if (fieldValues?.systemDefaults) {
      setSystemDefaults(mergePartialSystemDefaults(fieldValues.systemDefaults));
    }
  }, [fieldValues?.systemDefaults]);

  useEffect(() => {
    if (fieldValues?.housekeepingDefaults) {
      setHousekeepingDefaults(
        mergePartialHousekeepingDefaults(fieldValues.housekeepingDefaults),
      );
    }
  }, [fieldValues?.housekeepingDefaults]);

  useEffect(() => {
    if (fieldValues?.configuringGroupDefaults) {
      setConfiguringGroupDefaults(
        mergePartialConfiguringGroupDefaults(
          fieldValues.configuringGroupDefaults,
        ),
      );
    }
  }, [fieldValues?.configuringGroupDefaults]);

  useEffect(() => {
    dataRef.current?.(
      mergeQuestionnairePayload(
        stayDefaults,
        ageGroups,
        requiredFields,
        defaultCodes,
        systemDefaults,
        housekeepingDefaults,
        configuringGroupDefaults,
      ),
    );
  }, [
    stayDefaults,
    ageGroups,
    requiredFields,
    defaultCodes,
    systemDefaults,
    housekeepingDefaults,
    configuringGroupDefaults,
  ]);

  const updateGeneral = (patch: Partial<StayDefaultsGeneralPayload>) => {
    setStayDefaults((prev) => ({
      ...prev,
      general: { ...prev.general, ...patch },
    }));
  };

  const updateBookingConstraints = (
    patch: Partial<StayDefaultsBookingConstraintsPayload>,
  ) => {
    setStayDefaults((prev) => ({
      ...prev,
      bookingConstraints: { ...prev.bookingConstraints, ...patch },
    }));
  };

  const updateCancellation = (
    patch: Partial<StayDefaultsCancellationPayload>,
  ) => {
    setStayDefaults((prev) => ({
      ...prev,
      cancellation: { ...prev.cancellation, ...patch },
    }));
  };

  const updateGuestRequirements = (
    patch: Partial<StayDefaultsGuestRequirementsPayload>,
  ) => {
    setStayDefaults((prev) => ({
      ...prev,
      guestRequirements: { ...prev.guestRequirements, ...patch },
    }));
  };

  const updateBand = (index: number, patch: Partial<AgeGroupBand>) => {
    setAgeGroups((prev) => {
      const bands = [...prev.bands];
      bands[index] = { ...bands[index], ...patch };
      return { bands };
    });
  };

  const updateRequiredFields = (patch: Partial<RequiredFieldsPayload>) => {
    setRequiredFields((prev) => ({ ...prev, ...patch }));
  };

  const updateDefaultCodes = (patch: Partial<DefaultCodesPayload>) => {
    setDefaultCodes((prev) => ({ ...prev, ...patch }));
  };

  const updateSystemDefaults = (patch: Partial<SystemDefaultsPayload>) => {
    setSystemDefaults((prev) => ({ ...prev, ...patch }));
  };

  const updateHousekeepingDefaults = (
    patch: Partial<HousekeepingDefaultsPayload>,
  ) => {
    setHousekeepingDefaults((prev) => ({ ...prev, ...patch }));
  };

  const updateConfiguringGroupDefaults = (
    patch: Partial<ConfiguringGroupDefaultsPayload>,
  ) => {
    setConfiguringGroupDefaults((prev) => ({ ...prev, ...patch }));
  };

  const checkInDayjs: Dayjs | null = stayDefaults.general.checkInTime
    ? dayjs(`2000-01-01T${stayDefaults.general.checkInTime}`)
    : null;

  const checkOutDayjs: Dayjs | null = stayDefaults.general.checkOutTime
    ? dayjs(`2000-01-01T${stayDefaults.general.checkOutTime}`)
    : null;

  const businessDayStartDayjs: Dayjs | null =
    systemDefaults.businessDayStartTime
      ? dayjs(`2000-01-01T${systemDefaults.businessDayStartTime}`)
      : null;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Paper sx={{ padding: 2, width: "100%" }} ref={topRef}>
        <Stack spacing={2}>
          <Typography variant="h6" sx={textStyle}>
            Customer Questionnaire (Versa)
          </Typography>

          {/* 1. Stay defaults */}
          <Accordion defaultExpanded>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="versa-stay-defaults"
              id="versa-stay-defaults-header"
            >
              <Typography component="span" fontWeight={600}>
                Stay defaults
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Accordion defaultExpanded>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="stay-general"
                    id="stay-general-header"
                  >
                    <Typography component="span" fontWeight={600}>
                      When should guests check in and out, and in what timezone?
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={2}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                      >
                        <TimePicker
                          label="Check-in Time"
                          sx={{ width: "100%" }}
                          value={checkInDayjs}
                          onChange={(newValue: Dayjs | null) =>
                            updateGeneral({
                              checkInTime: newValue?.isValid()
                                ? newValue.format("HH:mm")
                                : null,
                            })
                          }
                        />
                        <TimePicker
                          label="Check-out Time"
                          sx={{ width: "100%" }}
                          value={checkOutDayjs}
                          onChange={(newValue: Dayjs | null) =>
                            updateGeneral({
                              checkOutTime: newValue?.isValid()
                                ? newValue.format("HH:mm")
                                : null,
                            })
                          }
                        />
                      </Stack>
                      <Autocomplete
                        freeSolo
                        options={PROPERTY_TIMEZONE_OPTIONS}
                        value={stayDefaults.general.timezone ?? null}
                        onChange={(_e, v) =>
                          updateGeneral({
                            timezone: typeof v === "string" ? v || null : null,
                          })
                        }
                        onInputChange={(_e, v, reason) => {
                          if (reason === "input" || reason === "clear") {
                            updateGeneral({ timezone: v || null });
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Timezone"
                            placeholder="e.g. America/New_York"
                          />
                        )}
                      />
                    </Stack>
                  </AccordionDetails>
                </Accordion>

                <Accordion>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="stay-booking"
                    id="stay-booking-header"
                  >
                    <Typography component="span" fontWeight={600}>
                      What are the minimum and maximum stay and lead-time rules?
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        label="Minimum Stay"
                        type="number"
                        fullWidth
                        value={stayDefaults.bookingConstraints.minStay ?? ""}
                        onChange={(e) =>
                          updateBookingConstraints({
                            minStay: e.target.value
                              ? Number(e.target.value)
                              : null,
                          })
                        }
                      />
                      <TextField
                        label="Maximum Stay"
                        type="number"
                        fullWidth
                        value={stayDefaults.bookingConstraints.maxStay ?? ""}
                        onChange={(e) =>
                          updateBookingConstraints({
                            maxStay: e.target.value
                              ? Number(e.target.value)
                              : null,
                          })
                        }
                      />
                      <TextField
                        label="Minimum Lead Time"
                        type="number"
                        fullWidth
                        value={
                          stayDefaults.bookingConstraints.minLeadTime ?? ""
                        }
                        onChange={(e) =>
                          updateBookingConstraints({
                            minLeadTime: e.target.value
                              ? Number(e.target.value)
                              : null,
                          })
                        }
                      />
                    </Stack>
                  </AccordionDetails>
                </Accordion>

                <Accordion>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="stay-cancellation"
                    id="stay-cancellation-header"
                  >
                    <Typography component="span" fontWeight={600}>
                      How should cancellations, refunds, and grace periods work?
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={2}>
                      <Typography variant="body2" component="p">
                        Which default cancellation policy should apply to new
                        bookings?
                      </Typography>
                      <FormControl fullWidth>
                        <InputLabel id="cancellation-policy-label">
                          Select a policy
                        </InputLabel>
                        <Select
                          labelId="cancellation-policy-label"
                          label="Select a policy"
                          value={stayDefaults.cancellation.policyType ?? ""}
                          onChange={(e) =>
                            updateCancellation({
                              policyType: (e.target.value as string) || null,
                            })
                          }
                        >
                          <MenuItem value="">
                            <em>Select…</em>
                          </MenuItem>
                          {CANCELLATION_POLICY_OPTIONS.map((opt) => (
                            <MenuItem key={opt} value={opt}>
                              {opt}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                      >
                        <TextField
                          label="If the policy allows a refund, what percentage of the stay should be refunded?"
                          type="number"
                          fullWidth
                          inputProps={{ min: 0, max: 100 }}
                          value={stayDefaults.cancellation.refundPercent ?? ""}
                          onChange={(e) =>
                            updateCancellation({
                              refundPercent: e.target.value
                                ? Number(e.target.value)
                                : null,
                            })
                          }
                        />
                        <TextField
                          label="After booking, how many hours should the free-cancellation grace period last?"
                          type="number"
                          fullWidth
                          value={
                            stayDefaults.cancellation.gracePeriodHours ?? ""
                          }
                          onChange={(e) =>
                            updateCancellation({
                              gracePeriodHours: e.target.value
                                ? Number(e.target.value)
                                : null,
                            })
                          }
                        />
                      </Stack>
                    </Stack>
                  </AccordionDetails>
                </Accordion>

                <Accordion>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="stay-guest-req"
                    id="stay-guest-req-header"
                  >
                    <Typography component="span" fontWeight={600}>
                      What must guests provide regarding age, ID, and photos?
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={2}>
                      <TextField
                        label="What is the minimum age required for the primary guest on a reservation?"
                        type="number"
                        fullWidth
                        value={stayDefaults.guestRequirements.minAge ?? ""}
                        onChange={(e) =>
                          updateGuestRequirements({
                            minAge: e.target.value
                              ? Number(e.target.value)
                              : null,
                          })
                        }
                      />
                      <FormControlLabel
                        labelPlacement="start"
                        sx={switchRowSx}
                        control={
                          <Switch
                            checked={stayDefaults.guestRequirements.idRequired}
                            onChange={(e) =>
                              updateGuestRequirements({
                                idRequired: e.target.checked,
                              })
                            }
                          />
                        }
                        label={
                          <Typography component="span" sx={{ pr: 1 }}>
                            Should guests be required to provide a
                            government-issued ID?
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        labelPlacement="start"
                        sx={switchRowSx}
                        control={
                          <Switch
                            checked={
                              stayDefaults.guestRequirements.photoRequired
                            }
                            onChange={(e) =>
                              updateGuestRequirements({
                                photoRequired: e.target.checked,
                              })
                            }
                          />
                        }
                        label={
                          <Typography component="span" sx={{ pr: 1 }}>
                            Should guests be required to provide or verify a
                            profile photo?
                          </Typography>
                        }
                      />
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* 2. Age groups */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="versa-age-groups"
              id="versa-age-groups-header"
            >
              <Typography component="span" fontWeight={600}>
                Age groups
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={3}>
                {ageGroups.bands.map((band, index) => (
                  <Stack
                    key={band.code}
                    spacing={2}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 2,
                    }}
                  >
                    <Typography variant="subtitle2" component="p">
                      For this age band, please answer the following:
                    </Typography>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        label="What system code should identify this age group?"
                        fullWidth
                        value={band.code}
                        onChange={(e) =>
                          updateBand(index, { code: e.target.value })
                        }
                      />
                      <TextField
                        label="What display label should guests and staff see for this group?"
                        fullWidth
                        value={band.label}
                        onChange={(e) =>
                          updateBand(index, { label: e.target.value })
                        }
                      />
                    </Stack>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        label="What is the minimum age (in years) included in this group?"
                        type="number"
                        fullWidth
                        value={band.minAgeInclusive ?? ""}
                        onChange={(e) =>
                          updateBand(index, {
                            minAgeInclusive: e.target.value
                              ? Number(e.target.value)
                              : null,
                          })
                        }
                      />
                      <TextField
                        label="What is the maximum age (in years) included in this group? (leave blank if no upper limit)"
                        type="number"
                        fullWidth
                        value={band.maxAgeInclusive ?? ""}
                        onChange={(e) =>
                          updateBand(index, {
                            maxAgeInclusive: e.target.value
                              ? Number(e.target.value)
                              : null,
                          })
                        }
                      />
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* 3. Required fields */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="versa-required-fields"
              id="versa-required-fields-header"
            >
              <Typography component="span" fontWeight={600}>
                Required fields
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <FormControlLabel
                  labelPlacement="start"
                  sx={switchRowSx}
                  control={
                    <Switch
                      checked={requiredFields.emailRequired}
                      onChange={(e) =>
                        updateRequiredFields({
                          emailRequired: e.target.checked,
                        })
                      }
                    />
                  }
                  label={
                    <Typography component="span" sx={{ pr: 1 }}>
                      Must every reservation include a valid guest email
                      address?
                    </Typography>
                  }
                />
                <FormControlLabel
                  labelPlacement="start"
                  sx={switchRowSx}
                  control={
                    <Switch
                      checked={requiredFields.phoneRequired}
                      onChange={(e) =>
                        updateRequiredFields({
                          phoneRequired: e.target.checked,
                        })
                      }
                    />
                  }
                  label={
                    <Typography component="span" sx={{ pr: 1 }}>
                      Must every reservation include a guest phone number?
                    </Typography>
                  }
                />
                <FormControlLabel
                  labelPlacement="start"
                  sx={switchRowSx}
                  control={
                    <Switch
                      checked={requiredFields.mailingAddressRequired}
                      onChange={(e) =>
                        updateRequiredFields({
                          mailingAddressRequired: e.target.checked,
                        })
                      }
                    />
                  }
                  label={
                    <Typography component="span" sx={{ pr: 1 }}>
                      Should a full mailing address be required at booking or
                      check-in?
                    </Typography>
                  }
                />
                <FormControlLabel
                  labelPlacement="start"
                  sx={switchRowSx}
                  control={
                    <Switch
                      checked={requiredFields.vehicleRegistrationRequired}
                      onChange={(e) =>
                        updateRequiredFields({
                          vehicleRegistrationRequired: e.target.checked,
                        })
                      }
                    />
                  }
                  label={
                    <Typography component="span" sx={{ pr: 1 }}>
                      Should vehicle or parking registration details be required
                      when applicable?
                    </Typography>
                  }
                />
                <FormControlLabel
                  labelPlacement="start"
                  sx={switchRowSx}
                  control={
                    <Switch
                      checked={requiredFields.emergencyContactRequired}
                      onChange={(e) =>
                        updateRequiredFields({
                          emergencyContactRequired: e.target.checked,
                        })
                      }
                    />
                  }
                  label={
                    <Typography component="span" sx={{ pr: 1 }}>
                      Must guests provide an emergency contact on file?
                    </Typography>
                  }
                />
                <TextField
                  label="Is there anything else we should capture about required guest or reservation fields? (optional)"
                  fullWidth
                  multiline
                  minRows={2}
                  value={requiredFields.notes ?? ""}
                  onChange={(e) =>
                    updateRequiredFields({
                      notes: e.target.value || null,
                    })
                  }
                />
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* 4. Default codes */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="versa-default-codes"
              id="versa-default-codes-header"
            >
              <Typography component="span" fontWeight={600}>
                Default codes
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="What default market code should new reservations use when none is specified?"
                    fullWidth
                    value={defaultCodes.defaultMarketCode ?? ""}
                    onChange={(e) =>
                      updateDefaultCodes({
                        defaultMarketCode: e.target.value || null,
                      })
                    }
                  />
                  <TextField
                    label="What default source-of-business code should apply?"
                    fullWidth
                    value={defaultCodes.defaultSourceCode ?? ""}
                    onChange={(e) =>
                      updateDefaultCodes({
                        defaultSourceCode: e.target.value || null,
                      })
                    }
                  />
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="What default origin code should apply?"
                    fullWidth
                    value={defaultCodes.defaultOriginCode ?? ""}
                    onChange={(e) =>
                      updateDefaultCodes({
                        defaultOriginCode: e.target.value || null,
                      })
                    }
                  />
                  <TextField
                    label="What default purpose-of-stay code should apply?"
                    fullWidth
                    value={defaultCodes.defaultPurposeOfStayCode ?? ""}
                    onChange={(e) =>
                      updateDefaultCodes({
                        defaultPurposeOfStayCode: e.target.value || null,
                      })
                    }
                  />
                </Stack>
                <TextField
                  label="What default guarantee or deposit code should apply?"
                  fullWidth
                  value={defaultCodes.defaultGuaranteeCode ?? ""}
                  onChange={(e) =>
                    updateDefaultCodes({
                      defaultGuaranteeCode: e.target.value || null,
                    })
                  }
                />
                <TextField
                  label="Any other notes about default codes? (optional)"
                  fullWidth
                  multiline
                  minRows={2}
                  value={defaultCodes.notes ?? ""}
                  onChange={(e) =>
                    updateDefaultCodes({ notes: e.target.value || null })
                  }
                />
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* 5. System defaults */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="versa-system-defaults"
              id="versa-system-defaults-header"
            >
              <Typography component="span" fontWeight={600}>
                System defaults
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <TimePicker
                  label="At what time should each business day roll over for reporting and night audit?"
                  sx={{ maxWidth: { sm: "100%", md: "50%" } }}
                  value={businessDayStartDayjs}
                  onChange={(newValue: Dayjs | null) =>
                    updateSystemDefaults({
                      businessDayStartTime: newValue?.isValid()
                        ? newValue.format("HH:mm")
                        : null,
                    })
                  }
                />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="Which currency code should be the property default (for example USD)?"
                    fullWidth
                    placeholder="USD"
                    value={systemDefaults.currencyCode ?? ""}
                    onChange={(e) =>
                      updateSystemDefaults({
                        currencyCode: e.target.value || null,
                      })
                    }
                  />
                  <TextField
                    label="Which date format should the property use by default?"
                    fullWidth
                    placeholder="e.g. MM/DD/YYYY"
                    value={systemDefaults.dateFormatCode ?? ""}
                    onChange={(e) =>
                      updateSystemDefaults({
                        dateFormatCode: e.target.value || null,
                      })
                    }
                  />
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="In which calendar month (1–12) does your fiscal year begin?"
                    type="number"
                    fullWidth
                    inputProps={{ min: 1, max: 12 }}
                    value={systemDefaults.fiscalYearStartMonth ?? ""}
                    onChange={(e) =>
                      updateSystemDefaults({
                        fiscalYearStartMonth: e.target.value
                          ? Number(e.target.value)
                          : null,
                      })
                    }
                  />
                  <TextField
                    label="Which locale or language code should be the default (for example en-US)?"
                    fullWidth
                    placeholder="en-US"
                    value={systemDefaults.localeCode ?? ""}
                    onChange={(e) =>
                      updateSystemDefaults({
                        localeCode: e.target.value || null,
                      })
                    }
                  />
                </Stack>
                <TextField
                  label="Any other notes about system defaults? (optional)"
                  fullWidth
                  multiline
                  minRows={2}
                  value={systemDefaults.notes ?? ""}
                  onChange={(e) =>
                    updateSystemDefaults({ notes: e.target.value || null })
                  }
                />
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* 6. Housekeeping defaults */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="versa-housekeeping-defaults"
              id="versa-housekeeping-defaults-header"
            >
              <Typography component="span" fontWeight={600}>
                Housekeeping defaults
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="What default housekeeping service frequency code should rooms use?"
                    fullWidth
                    value={
                      housekeepingDefaults.defaultServiceFrequencyCode ?? ""
                    }
                    onChange={(e) =>
                      updateHousekeepingDefaults({
                        defaultServiceFrequencyCode: e.target.value || null,
                      })
                    }
                  />
                  <TextField
                    label="How many nights should pass between full cleans by default?"
                    type="number"
                    fullWidth
                    value={housekeepingDefaults.fullCleanIntervalNights ?? ""}
                    onChange={(e) =>
                      updateHousekeepingDefaults({
                        fullCleanIntervalNights: e.target.value
                          ? Number(e.target.value)
                          : null,
                      })
                    }
                  />
                </Stack>
                <FormControlLabel
                  labelPlacement="start"
                  sx={switchRowSx}
                  control={
                    <Switch
                      checked={housekeepingDefaults.turndownServiceDefault}
                      onChange={(e) =>
                        updateHousekeepingDefaults({
                          turndownServiceDefault: e.target.checked,
                        })
                      }
                    />
                  }
                  label={
                    <Typography component="span" sx={{ pr: 1 }}>
                      Should turndown service be turned on by default for
                      eligible stays?
                    </Typography>
                  }
                />
                <FormControlLabel
                  labelPlacement="start"
                  sx={switchRowSx}
                  control={
                    <Switch
                      checked={
                        housekeepingDefaults.respectDoNotDisturbForService
                      }
                      onChange={(e) =>
                        updateHousekeepingDefaults({
                          respectDoNotDisturbForService: e.target.checked,
                        })
                      }
                    />
                  }
                  label={
                    <Typography component="span" sx={{ pr: 1 }}>
                      Should housekeeping skip service while Do Not Disturb is
                      active?
                    </Typography>
                  }
                />
                <TextField
                  label="Any other notes about housekeeping defaults? (optional)"
                  fullWidth
                  multiline
                  minRows={2}
                  value={housekeepingDefaults.notes ?? ""}
                  onChange={(e) =>
                    updateHousekeepingDefaults({
                      notes: e.target.value || null,
                    })
                  }
                />
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* 7. Configuring group defaults */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="versa-configuring-group-defaults"
              id="versa-configuring-group-defaults-header"
            >
              <Typography component="span" fontWeight={600}>
                Configuring group defaults
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <TextField
                  label="What default configuration group code should new inventory or room setups use?"
                  fullWidth
                  value={
                    configuringGroupDefaults.defaultConfigurationGroupCode ?? ""
                  }
                  onChange={(e) =>
                    updateConfiguringGroupDefaults({
                      defaultConfigurationGroupCode: e.target.value || null,
                    })
                  }
                />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="What default inventory group code should apply?"
                    fullWidth
                    value={
                      configuringGroupDefaults.defaultInventoryGroupCode ?? ""
                    }
                    onChange={(e) =>
                      updateConfiguringGroupDefaults({
                        defaultInventoryGroupCode: e.target.value || null,
                      })
                    }
                  />
                  <TextField
                    label="What default pricing group code should apply?"
                    fullWidth
                    value={
                      configuringGroupDefaults.defaultPricingGroupCode ?? ""
                    }
                    onChange={(e) =>
                      updateConfiguringGroupDefaults({
                        defaultPricingGroupCode: e.target.value || null,
                      })
                    }
                  />
                </Stack>
                <FormControlLabel
                  labelPlacement="start"
                  sx={switchRowSx}
                  control={
                    <Switch
                      checked={
                        configuringGroupDefaults.allowSplitInventoryAcrossGroups
                      }
                      onChange={(e) =>
                        updateConfiguringGroupDefaults({
                          allowSplitInventoryAcrossGroups: e.target.checked,
                        })
                      }
                    />
                  }
                  label={
                    <Typography component="span" sx={{ pr: 1 }}>
                      Should the system allow inventory to be split across
                      configuration groups when needed?
                    </Typography>
                  }
                />
                <TextField
                  label="Any other notes about configuring group defaults? (optional)"
                  fullWidth
                  multiline
                  minRows={2}
                  value={configuringGroupDefaults.notes ?? ""}
                  onChange={(e) =>
                    updateConfiguringGroupDefaults({
                      notes: e.target.value || null,
                    })
                  }
                />
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Stack>
      </Paper>
    </LocalizationProvider>
  );
};

export default VersaQuestionnaire;
